<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import { BFormInput } from 'bootstrap-vue-next';
import { vMaska } from 'maska/vue';
import { required as requiredRule } from '@regle/rules';
import {
  useItpFormControl,
  useItpFormField,
  useItpFormValidation,
} from './useItpForm';
import { useNumberMask } from './useNumberMask';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  modelValue?: string | number | null;
  name: string;
  id?: string;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  type?: 'text' | 'number' | 'email' | 'password';
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  mask?: string | Record<string, any>;
  unmask?: string | boolean;
  scale?: number | string;
  min?: number | string;
  max?: number | string;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', v: string | number | null): void;
  (event: 'input', v: string | number | null): void;
}>();

const attrs = useAttrs();
const inputAttrs = computed(() => {
  const { value, modelValue, ...rest } = attrs as any;
  return rest;
});

const { isDisabled, isReadonly } = useItpFormControl(props);
const { touch } = useItpFormField(props, (isRequired) =>
  isRequired ? { required: requiredRule } : {},
);

const isNumberMask = props.mask === 'number';
const maskedInputRef = ref<HTMLInputElement | null>(null);

const { isInvalid } = useItpFormValidation(props.name);

if (isNumberMask) {
  useNumberMask(maskedInputRef, props, emit);
}

const sizeClass = computed(() => {
  if (!props.size || props.size === 'md') {
    return '';
  }
  return `form-control-${props.size}`;
});

const modelProxy = computed<any>({
  get: () => props.modelValue,
  set: (next) => {
    let out = next;
    if (props.type === 'number') {
      if (out === '' || out == null) {
        out = null;
      } else if (typeof out === 'string') {
        out = Number(out.replace(/\s/g, '').replace(/,/g, '.'));
      }
    }
    emit('update:modelValue', out);
    emit('input', out);
  },
});
</script>

<template>
  <input
    v-if="isNumberMask"
    :id="id || name"
    ref="maskedInputRef"
    :name="name"
    :placeholder="placeholder"
    :disabled="isDisabled"
    :readonly="isReadonly"
    class="form-control"
    :class="[sizeClass, { 'is-invalid': isInvalid }]"
    type="text"
    inputmode="decimal"
    v-bind="inputAttrs"
    @blur="touch"
  />
  <BFormInput
    v-else-if="mask"
    :id="id || name"
    v-model="modelProxy"
    v-maska="mask"
    :name="name"
    :type="type"
    :size="size"
    :placeholder="placeholder"
    :disabled="isDisabled"
    :readonly="isReadonly"
    v-bind="inputAttrs"
    @blur="touch"
  />
  <BFormInput
    v-else
    :id="id || name"
    v-model="modelProxy"
    :name="name"
    :type="type"
    :size="size"
    :placeholder="placeholder"
    :disabled="isDisabled"
    :readonly="isReadonly"
    v-bind="inputAttrs"
    @blur="touch"
  />
</template>
